import * as React from 'react';

import { useTranslations } from '@rematter/modules/trade/i18n/useTranslations';
import { InventoryItemRecord } from '@rematter/modules/trade/types/load';
import { Button } from '@rematter/ui-web';
import { LucideChevronDown, LucideChevronUp } from 'lucide-react';

import { InventoryItemButton } from '../InventoryItemButton';

type HighlightedMaterialsSectionProps = {
  materials: InventoryItemRecord[];
  sectionTitle: string;
  onSelectMaterial: (materialId: string) => void;
};

export const HighlightedMaterialsSection = ({
  materials,
  sectionTitle,
  onSelectMaterial,
}: HighlightedMaterialsSectionProps) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const t = useTranslations();

  if (materials.length === 0) {
    return null;
  }

  return (
    <div className="@HighlightedMaterialsSection flex flex-col gap-4 pb-2">
      <div className="flex cursor-pointer items-center justify-between">
        <div className="flex flex-col items-start justify-center gap-2">
          <span className="text-xl font-semibold">{sectionTitle}</span>
          <span className="text-neutral/60">
            {materials.length}{' '}
            {t.inventoryItemsModal.materialLength(materials.length)}
          </span>
        </div>
        <div className="mr-2 flex size-8 items-center justify-center rounded bg-neutral/5">
          <Button
            variant="outline"
            color="neutral"
            className="border-neutral px-2 py-3"
            onPress={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <LucideChevronUp size={24} className="text-neutral/60" />
            ) : (
              <LucideChevronDown size={24} className="text-neutral/60" />
            )}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="grid grid-cols-3 gap-2">
          {materials.map((material) => (
            <InventoryItemButton
              key={material.id}
              isSelected={false}
              inventoryItem={material}
              onPress={() => onSelectMaterial(material.id)}
              leftElement={material.mnemonic}
              align="left"
              variant="subtle"
            />
          ))}
        </div>
      )}

      <hr className="m-0 border-t-2 border-neutral/15" />
    </div>
  );
};
